import { useCallback, useMemo, useState } from "react";

import SimpleModal from "../../../../shared/components/simple-modal/simple-modal";
import { noop } from "../../../utils";

import "./document-modal.scss";
import { useDropzone } from "react-dropzone";

interface IDocumentModalProps {
  showModal: boolean;
  onModalSubmit: (files: File[]) => void;
  onModalClosed: () => void;
}

const UNSCANNABLE_EXTS = new Set(["doc", "xls", "png", "jpg", "jpeg", "webp"]);

function getExt(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
}
const DocumentModal = ({ showModal, onModalSubmit, onModalClosed }: IDocumentModalProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [showInfo, setShowInfo] = useState(false);

  const hasUnscannable = useMemo(
    () => files.some(f => UNSCANNABLE_EXTS.has(getExt(f.name))),
    [files]
  );

  const unscannableFiles = useMemo(
    () => files.filter(f => UNSCANNABLE_EXTS.has(getExt(f.name))).map(f => f.name),
    [files]
  );

  const handleModalSubmit = () => {
    onModalSubmit(files);
    setFiles([]);
    setShowInfo(false);
  };

  const handleModalClosed = () => {
    setFiles([]);
    setShowInfo(false);
    onModalClosed();
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(prevFiles => {
      const existingNames = new Set(prevFiles.map(file => file.name));
      const newUniqueFiles = acceptedFiles.filter(file => !existingNames.has(file.name));
      const next = [...prevFiles, ...newUniqueFiles];
      if (newUniqueFiles.some(f => UNSCANNABLE_EXTS.has(getExt(f.name)))) {
        setShowInfo(true);
      }
      return next;
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
  });

  const renderInfoBody = () => (
    <div className="documentModal-info">
      <p>
        Some of the files you added can be <strong>saved</strong>, but we are
        <strong> unable to scan or extract text content</strong> from these formats:
      </p>
      <ul className="documentModal-infoList">
        <li><code>.doc</code> — Please try converting to <strong>.docx</strong></li>
        <li><code>.xls</code> — Please try converting to <strong>.xlsx</strong></li>
        <li><code>Images</code> cannot be scanned</li>
      </ul>
      {unscannableFiles.length > 0 && (
        <div>
          <p>These files are affected:</p>
          <ul className="documentModal-fileList">
            {unscannableFiles.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
        </div>
      )}
      <p>
        You can still upload them and we’ll store them safely, but they won’t
        appear in document search results based on their content.
      </p>
    </div>
  );

  const renderDocumentModalBody = () => {
    return (
      <div className="documentModal">
        <div
          {...getRootProps()}
          className={`documentModal-dropzone ${isDragActive ? " documentModal-dropzoneDragActive" : ""}`}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <p>Drop the files here…</p>
          ) : (
            <p>Drag & drop files here, or click to select</p>
          )}
        </div>

        {files.length > 0 && (
          <div>
            <p>Selected files for upload:</p>
            <ul>
              {files.map((file, idx) => {
                const ext = getExt(file.name);
                const unscannable = UNSCANNABLE_EXTS.has(ext);
                return (
                  <li className="documentModal-file" key={idx}>
                    <span
                      className="documentModal-remove"
                      onClick={() => setFiles(prev => prev.filter(f => f.name !== file.name))}
                      title="Remove"
                    >
                      ×
                    </span>
                    <p>
                      {file.name}
                      {unscannable && (
                        <span className="documentModal-chip documentModal-chip--warn" title="Content cannot be scanned"> - 
                           (Not indexed for search)
                        </span>
                      )}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="documentModal">
      {/* Main upload modal */}
      <SimpleModal
        mode="Add documents"
        onModalClickHandler={noop}
        modalBody={renderDocumentModalBody()}
        buttonText={"Add"}
        handleModalClosed={handleModalClosed}
        handleSubmit={handleModalSubmit}
        show={showModal}
      />
      <SimpleModal
        mode="Information"
        onModalClickHandler={() => setShowInfo(false)}
        modalBody={renderInfoBody()}
        buttonText={"Okay"}
        handleModalClosed={() => setShowInfo(false)}
        handleSubmit={() => setShowInfo(false)}
        show={showInfo && showModal && hasUnscannable}
        showCancelButton={false}
      />
    </div>
  );
};

export default DocumentModal;
